// Zoom-style share picker for the SELF room.
//   Screens: Entire screen · A window · A Chrome tab
//   More:    Video file · Second camera
//   Layout:  Content only · Side by side · Over the shoulder (your camera with the content)
//   Options: Share sound · Optimize for video
// A layout with the camera goes through a frame pipeline, driven by the source's own frames
// rather than a timer, so it keeps running while the sharer works in another window.
// _removed: "Portion of screen" (share the whole screen, then drag a box on a preview) ·
// 2026-10-07, Kalvin's first test: Chrome's picker and the macOS Screen Recording permission got
// in the way, and the box stayed put when a window moved · "A window" covers it: it follows the
// app. A browser can't put Zoom's movable frame on the desktop; that needs a desktop app.
import { Track } from "livekit-client";

const $ = id => document.getElementById(id);
const S = { room: null, source: null, cam: null, out: [], pubs: [], layout: "content", stopFns: [] };
let onChange = () => {};

const TILES = {
  screens: [
    { id: "monitor", icon: "🖥️", label: "Entire screen" },
    { id: "window", icon: "🗔", label: "A window" },
    { id: "browser", icon: "🌐", label: "A Chrome tab" },
  ],
  more: [
    { id: "file", icon: "🎞️", label: "Video file" },
    { id: "camera2", icon: "📷", label: "Second camera" },
  ],
};
const LAYOUTS = [
  { id: "content", label: "Content only" },
  { id: "side", label: "Side by side" },
  { id: "shoulder", label: "Over the shoulder" },
];

export function initShare(room, changed) {
  S.room = room; onChange = changed;
  $("sharepicker").addEventListener("click", e => {
    const t = e.target.closest("[data-tab]"); if (t) return tab(t.dataset.tab);
    const tile = e.target.closest("[data-src]"); if (tile) return pick(tile.dataset.src);
    const lay = e.target.closest("[data-layout]"); if (lay) { S.layout = lay.dataset.layout; drawPicker(); }
  });
  $("sharego").onclick = () => start().catch(err => { if (err?.name !== "NotAllowedError") alert(`Couldn't share: ${err.message}`); stop(); });
  $("sharecancel").onclick = () => $("sharepicker").classList.add("hidden");
}

export const sharing = () => S.pubs.length > 0;

let curTab = "screens", curSrc = "monitor";
export function openPicker() { drawPicker(); $("sharepicker").classList.remove("hidden"); }
function tab(t) { curTab = t; curSrc = TILES[t][0].id; drawPicker(); }
function pick(id) { curSrc = id; drawPicker(); }

function drawPicker() {
  const camOn = !!S.room.localParticipant.getTrackPublication(Track.Source.Camera)?.track;
  $("sharetabs").innerHTML = ["screens", "more"].map(t =>
    `<button data-tab="${t}" class="${t === curTab ? "on" : ""}">${t === "screens" ? "Screens" : "More"}</button>`).join("");
  $("sharetiles").innerHTML = TILES[curTab].map(x =>
    `<button data-src="${x.id}" class="tile2 ${x.id === curSrc ? "on" : ""}" title="${x.help || ""}"><span class="ic">${x.icon}</span>${x.label}</button>`).join("");
  $("sharelayouts").innerHTML = LAYOUTS.map(l =>
    `<button data-layout="${l.id}" class="lay ${l.id === S.layout ? "on" : ""}" ${l.id !== "content" && !camOn ? "disabled title='Turn your camera on to use this'" : ""}>
       <span class="lp lp-${l.id}"><i></i><b></b></span>${l.label}</button>`).join("");
  $("sharehelp").textContent = curSrc === "window" ? "Shares one app, and follows it if you move the window. Use this to show part of your screen."
    : curSrc === "file" ? "Plays a video from your computer to the room, with its sound."
    : curSrc === "camera2" ? "Shares another camera, e.g. a document camera or a whiteboard." : "";
  $("camera2pick").classList.toggle("hidden", curSrc !== "camera2");
  if (curSrc === "camera2") listCameras();
}

async function listCameras() {
  const devs = (await navigator.mediaDevices.enumerateDevices()).filter(d => d.kind === "videoinput");
  $("camera2pick").innerHTML = devs.map(d => `<option value="${d.deviceId}">${d.label || "Camera"}</option>`).join("");
}

// ---------------------------------------------------------------- start / stop

async function start() {
  const sound = $("sharesound").checked, motion = $("sharemotion").checked;
  $("sharepicker").classList.add("hidden");
  await stop(true);
  const fr = motion ? 30 : 15;
  if (["monitor", "window", "browser"].includes(curSrc)) {
    S.source = await navigator.mediaDevices.getDisplayMedia({
      video: { frameRate: fr, displaySurface: curSrc },
      audio: sound, selfBrowserSurface: "exclude", surfaceSwitching: "include",
      monitorTypeSurfaces: "include", systemAudio: sound ? "include" : "exclude", preferCurrentTab: false,
    });
  } else if (curSrc === "file") {
    S.source = await videoFileStream();
  } else if (curSrc === "camera2") {
    S.source = await navigator.mediaDevices.getUserMedia({ video: { deviceId: { exact: $("camera2pick").value } }, audio: false });
  }
  const v = S.source.getVideoTracks()[0];
  v.addEventListener("ended", () => stop());
  if (S.layout !== "content") {
    const camTrack = S.room.localParticipant.getTrackPublication(Track.Source.Camera)?.track?.mediaStreamTrack;
    S.cam = camTrack ? camTrack.clone() : null;
  }
  const out = S.cam ? pipeline(v) : v;
  if (motion && "contentHint" in out) out.contentHint = "motion"; else if ("contentHint" in out) out.contentHint = "detail";
  S.out = [out];
  S.pubs.push(await S.room.localParticipant.publishTrack(out, { source: Track.Source.ScreenShare, name: `share-${curSrc}`, simulcast: false }));
  const a = S.source.getAudioTracks()[0];
  if (a) { S.out.push(a); S.pubs.push(await S.room.localParticipant.publishTrack(a, { source: Track.Source.ScreenShareAudio, name: "share-sound" })); }
  onChange();
}

export async function stop(quiet) {
  for (const t of S.out) { try { await S.room.localParticipant.unpublishTrack(t); } catch {} }
  for (const f of S.stopFns) f();
  S.source?.getTracks().forEach(t => t.stop());
  S.cam?.stop();
  Object.assign(S, { source: null, cam: null, out: [], pubs: [], stopFns: [] });
  if (!quiet) onChange();
}

function videoFileStream() {
  return new Promise((resolve, reject) => {
    const inp = document.createElement("input");
    inp.type = "file"; inp.accept = "video/*";
    inp.onchange = async () => {
      const f = inp.files?.[0]; if (!f) return reject(Object.assign(new Error("no file"), { name: "NotAllowedError" }));
      const v = document.createElement("video");
      v.src = URL.createObjectURL(f); v.playsInline = true;
      await v.play();
      S.stopFns.push(() => { v.pause(); URL.revokeObjectURL(v.src); });
      v.onended = () => stop();
      resolve(v.captureStream ? v.captureStream() : v.mozCaptureStream());
    };
    inp.click();
  });
}

// ---------------------------------------------------------------- frame pipeline (layout)

// Fractions → whole, even pixels inside the frame (encoders want even sizes).
// The whole frame, trimmed to even sizes for the encoder.
const full = (W, H) => ({ x: 0, y: 0, width: W - (W % 2), height: H - (H % 2) });

function pipeline(src) {
  // Latest camera frame, kept for the layouts.
  let camFrame = null;
  if (S.cam && window.MediaStreamTrackProcessor) {
    const reader = new MediaStreamTrackProcessor({ track: S.cam }).readable.getReader();
    (async () => { for (;;) { const { value, done } = await reader.read(); if (done) break; camFrame?.close(); camFrame = value; } })().catch(() => {});
    S.stopFns.push(() => { reader.cancel().catch(() => {}); camFrame?.close(); });
  }
  const oc = new OffscreenCanvas(2, 2), g = oc.getContext("2d");
  const compose = (frame, W, H) => {
    const r = full(W, H);
    if (S.layout === "side" && camFrame) {
      const ch = r.height, cw = Math.round(ch * camFrame.displayWidth / camFrame.displayHeight / 2) * 2;
      size(r.width + cw, ch); g.fillStyle = "#111"; g.fillRect(0, 0, oc.width, oc.height);
      g.drawImage(frame, r.x, r.y, r.width, r.height, 0, 0, r.width, r.height);
      g.drawImage(camFrame, r.width, 0, cw, ch);
    } else {
      size(r.width, r.height);
      g.drawImage(frame, r.x, r.y, r.width, r.height, 0, 0, r.width, r.height);
      if (S.layout === "shoulder" && camFrame) {
        const w = Math.round(r.width * 0.24), h = Math.round(w * camFrame.displayHeight / camFrame.displayWidth);
        const x = r.width - w - 16, y = r.height - h - 16;
        g.save(); g.beginPath(); g.roundRect(x, y, w, h, 14); g.clip(); g.drawImage(camFrame, x, y, w, h); g.restore();
        g.strokeStyle = "rgba(255,255,255,.9)"; g.lineWidth = 3; g.beginPath(); g.roundRect(x, y, w, h, 14); g.stroke();
      }
    }
  };
  function size(w, h) { if (oc.width !== w || oc.height !== h) { oc.width = w; oc.height = h; } }

  if (window.MediaStreamTrackProcessor && window.MediaStreamTrackGenerator) {
    const proc = new MediaStreamTrackProcessor({ track: src });
    const gen = new MediaStreamTrackGenerator({ kind: "video" });
    proc.readable.pipeThrough(new TransformStream({
      transform(frame, ctl) {
        compose(frame, frame.displayWidth, frame.displayHeight);
        const out = new VideoFrame(oc, { timestamp: frame.timestamp });
        frame.close();
        ctl.enqueue(out);
      },
    })).pipeTo(gen.writable).catch(() => {});
    return gen;
  }
  // Fallback (Safari): a canvas on a timer (pauses if the tab is hidden).
  const v = document.createElement("video"); v.srcObject = new MediaStream([src]); v.muted = true; v.play();
  const c = document.createElement("canvas"), cg = c.getContext("2d");
  const id = setInterval(() => { if (!v.videoWidth) return; compose(v, v.videoWidth, v.videoHeight); c.width = oc.width; c.height = oc.height; cg.drawImage(oc, 0, 0); }, 66);
  S.stopFns.push(() => clearInterval(id));
  return c.captureStream(15).getVideoTracks()[0];
}
